import { createContext, useContext, useState, FC, ReactNode, useMemo } from "react";

export enum InviteExistingUserStepEnum {
  AcceptanceStep = 0,
  ConfirmationStep = 1
}

export type ExistingInviteVariant = "external" | "primary";

export type MoveResult = "accepted" | "rejected" | null;

interface InviteExistingUserContextFlowContextProps {
  currentStep: InviteExistingUserStepEnum;
  setCurrentStep: (s: InviteExistingUserStepEnum) => void;
  organization: string | null;
  setOrganization: (o: string | null) => void;
  currentOrganization: string | null;
  setCurrentOrganization: (o: string | null) => void;
  moveResult: "accepted" | "rejected" | null;
  setMoveResult: (r: "accepted" | "rejected" | null) => void;
  userEmail: string | null;
  setUserEmail: (email: string | null) => void;
  variant: ExistingInviteVariant;
  setVariant: (v: ExistingInviteVariant) => void;
  invitationState: string | null;
  setInvitationState: (i: string | null) => void;
  resetFlow: () => void;
}

const InviteExistingUserFlowContext = createContext<
  InviteExistingUserContextFlowContextProps | undefined
>(undefined);

export const useInviteExistingUserFlow = () => {
  const context = useContext(InviteExistingUserFlowContext);
  if (!context) {
    throw new Error(
      "useInviteExistingUserFlow must be used within a InviteExistingUserFlowProvider"
    );
  }
  return context;
};

export const InviteExistingUserFlowProvider: FC<{
  children: ReactNode;
  initialVariant: ExistingInviteVariant;
}> = ({ children, initialVariant }) => {
  const [currentStep, setCurrentStep] = useState(InviteExistingUserStepEnum.AcceptanceStep);
  const [organization, setOrganization] = useState<string | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<string | null>(null);
  const [moveResult, setMoveResult] = useState<"accepted" | "rejected" | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [invitationState, setInvitationState] = useState<string | null>(null);

  const [variant, setVariant] = useState<ExistingInviteVariant>(initialVariant);

  const resetFlow = () => {
    setCurrentStep(InviteExistingUserStepEnum.AcceptanceStep);
    setOrganization(null);
    setCurrentOrganization(null);
    setMoveResult(null);
    setUserEmail(null);
    setInvitationState(null);
  };

  const value = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      organization,
      setOrganization,
      currentOrganization,
      setCurrentOrganization,
      moveResult,
      setMoveResult,
      variant,
      setVariant,
      userEmail,
      setUserEmail,
      invitationState,
      setInvitationState,
      resetFlow
    }),
    [
      currentStep,
      organization,
      currentOrganization,
      moveResult,
      variant,
      userEmail,
      invitationState
    ]
  );

  return (
    <InviteExistingUserFlowContext.Provider value={value}>
      {children}
    </InviteExistingUserFlowContext.Provider>
  );
};
